import {
  channelLookupUrl,
  clipWatchRoute,
  formatRoute,
  jumpOutLinks,
  routeForResolvedTarget,
  type ChannelNameForm,
  type ChannelTab,
  type ClipTarget,
  type JumpOut,
  type ResolvedTarget,
  type Result,
  type Route,
} from '@visious/core';
import { describeProblem, type ProblemCopy } from '../../api/problem';
import { text } from '../../strings';
import type { ResolveProblem } from '../../api/resolve';

/**
 * §8's URL-scheme rows that cost a request: `/@handle`, `/c/NAME`, `/user/NAME` and `/clip/ID`.
 * R13 — every decision here, no DOM.
 *
 * **It is one screen for two endpoints** because it is one *state machine*: work out what has to be
 * asked, ask it, and either navigate or explain. The alternative — resolving inside the channel screen
 * — was rejected for the reason §8 gives for keeping `/post/ID` out of the 404: a failed resolution and
 * an empty channel are different answers, and a page that can render the second will render it for the
 * first.
 *
 * The navigation is a **replace**, not a push. Without that, Back from the channel returns to the
 * resolver, which resolves again and pushes forward — the reader is trapped one press from where they
 * were, and it looks like Back is broken rather than like the URL scheme is.
 */

/**
 * Re-exported so `view.tsx` can name its prop without importing core, which R13 forbids it. The same
 * seam `screens/channel/model.ts` uses for `ChannelTab`.
 */
export type ResolveRoute = Route;

const HOME_HREF = formatRoute({ kind: 'home' });

export interface ResolveRequest {
  /** `null` for a clip: the clips endpoint takes the id, not a YouTube URL. */
  readonly youtubeUrl: string | null;
  readonly clipId: string | null;
  /** Carried through so `/@handle/shorts` lands on Shorts rather than on Videos. */
  readonly tab: ChannelTab;
}

/**
 * What has to be asked, for a route. `null` means this screen was handed a route it does not serve,
 * which is a programming error rather than a reader-visible state — `screenForRoute` only routes the
 * three arms below here.
 */
export function resolveRequestFor(route: Route): ResolveRequest | null {
  if (route.kind === 'channelName') {
    const youtubeUrl = channelLookupUrl(route);
    return youtubeUrl === null ? null : { youtubeUrl, clipId: null, tab: route.tab };
  }
  if (route.kind === 'clip') {
    return { youtubeUrl: null, clipId: route.clipId, tab: 'videos' };
  }
  return null;
}

/** What the reader is waiting for, in words, while the request is out. */
export interface ResolveSubject {
  readonly label: string;
  readonly detail: string;
}

export function resolveSubjectFor(route: Route): ResolveSubject {
  const words = text().resolve.subject;
  if (route.kind === 'channelName') {
    return { label: displayName(route.form, route.name), detail: words.channelName };
  }
  if (route.kind === 'clip') {
    return { label: words.clipLabel, detail: words.clip };
  }
  return { label: words.linkLabel, detail: words.link };
}

function displayName(form: ChannelNameForm, name: string): string {
  return form === 'handle' ? text().resolve.subject.handle(name) : name;
}

export type ResolveState =
  | { readonly kind: 'resolving'; readonly subject: ResolveSubject }
  /**
   * Resolved — the caller navigates to `href` and this page is gone.
   *
   * There is deliberately nothing else on this arm. §8 asks for a clip to resolve "with the clip's
   * start/end" and **only the start is representable**: `/watch` has no end parameter in YouTube's URL
   * scheme or in ours, so carrying one would mean inventing a parameter and breaking the domain-swap
   * property the section exists for. An earlier draft put the bounds in a `note` here — which nothing
   * could render, because a page whose job is to redirect has no moment in which to be read. The
   * limitation is recorded in docs/specs/known-gaps.md instead of decorating a state that lasts one frame.
   */
  | { readonly kind: 'ready'; readonly href: string }
  | {
      readonly kind: 'problem';
      readonly subject: ResolveSubject;
      readonly copy: ProblemCopy;
      /** §8 wants a jump-out on a page that cannot answer. YouTube needs no instance to link to. */
      readonly jumpOuts: readonly JumpOut[];
      readonly homeHref: string;
    };

export interface ResolveStateInput {
  readonly route: Route;
  readonly target: Result<ResolvedTarget, ResolveProblem> | null;
  readonly clip: Result<ClipTarget, ResolveProblem> | null;
}

export function resolveStateFor(input: ResolveStateInput): ResolveState {
  const subject = resolveSubjectFor(input.route);
  const request = resolveRequestFor(input.route);

  if (request === null) {
    return {
      kind: 'problem',
      subject,
      copy: text().resolve.notResolvable,
      jumpOuts: jumpOutLinks(null, input.route),
      homeHref: HOME_HREF,
    };
  }

  if (request.clipId !== null) return clipStateFor(input, subject);
  return nameStateFor(input, subject, request.tab);
}

function nameStateFor(
  input: ResolveStateInput,
  subject: ResolveSubject,
  tab: ChannelTab,
): ResolveState {
  if (input.target === null) return { kind: 'resolving', subject };

  if (!input.target.ok) {
    return {
      kind: 'problem',
      subject,
      copy: resolveCopy(input.target.error, channelNameProblem(subject.label), subject.label),
      jumpOuts: jumpOutLinks(null, input.route),
      homeHref: HOME_HREF,
    };
  }

  const target = input.target.value;
  const route = routeForResolvedTarget(target, { tab });
  if (route === null) {
    return {
      kind: 'problem',
      subject,
      // The `unrecognised` arm, and the copy has to say what happened rather than "not found": the
      // instance answered `200`. An unknown URL resolves to `FEwhat_to_watch` — YouTube's own home
      // feed — so a successful request is not a successful resolution.
      copy: {
        title: text().resolve.unrecognisedTitle(subject.label),
        detail: text().resolve.unrecognisedDetail(
          target.kind === 'unrecognised' ? target.pageType : text().resolve.unknownPageType,
        ),
        retryable: false,
      },
      jumpOuts: jumpOutLinks(null, input.route),
      homeHref: HOME_HREF,
    };
  }

  return { kind: 'ready', href: formatRoute(route) };
}

function clipStateFor(input: ResolveStateInput, subject: ResolveSubject): ResolveState {
  if (input.clip === null) return { kind: 'resolving', subject };

  if (!input.clip.ok) {
    return {
      kind: 'problem',
      subject,
      copy: resolveCopy(input.clip.error, clipProblem(), 'this clip'),
      jumpOuts: jumpOutLinks(null, input.route),
      homeHref: HOME_HREF,
    };
  }

  return { kind: 'ready', href: formatRoute(clipWatchRoute(input.clip.value)) };
}

function channelNameProblem(label: string) {
  const words = text().resolve.channelName;
  return {
    // A relayed YouTube 404, which `relayedUpstreamStatus` already maps here — so this is
    // the reachable failure rather than the theoretical one: `/@ThisHandleDoesNotExist` answers `500`
    // carrying YouTube's own 404.
    notFound: {
      title: words.notFoundTitle(label),
      detail: words.notFoundDetail,
      retryable: false,
    },
    unreadable: (): ProblemCopy => ({
      title: words.unreadableTitle(label),
      detail: words.unreadableDetail,
      retryable: false,
    }),
    upstreamBroken: {
      title: words.upstreamBrokenTitle(label),
      detail: words.upstreamBrokenDetail,
      retryable: false,
    },
  };
}

function clipProblem() {
  const words = text().resolve.clip;
  return {
    notFound: words.notFound,
    unreadable: (): ProblemCopy => ({
      title: words.unreadableTitle,
      detail: words.unreadableDetail,
      retryable: false,
    }),
  };
}

/**
 * The taxonomy, plus the one arm that is a *resolution* failure rather than an API failure and so was
 * never in it: **`/api/v1/clips/{malformed}` answers `200` with zero bytes.** That is a sentence about
 * the link, not about the instance, and the shared `malformed-json` copy ("usually a proxy or captive
 * portal answering instead of Invidious") would send a reader off to check their network for a mistyped
 * ID.
 */
function resolveCopy(
  problem: ResolveProblem,
  subject: Parameters<typeof describeProblem>[1],
  label: string,
): ProblemCopy {
  if (problem.kind === 'not-resolvable') {
    return {
      title: text().resolve.notFollowedTitle(label),
      detail: text().resolve.notFollowedDetail,
      retryable: false,
    };
  }
  return describeProblem(problem, subject);
}
